import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import Cookies from "js-cookie";
import { QRCodeSVG } from "qrcode.react";
import { useEffect, useState } from "react";
import AwsApiClient from "../../../../../../hooks/AwsApiClient";

const MFASetupDialog = ({ open, onClose }) => {
  const [step, setStep] = useState("LOADING");
  const [secretCode, setSecretCode] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const client = new AwsApiClient();

  useEffect(() => {
    if (open) {
      checkMfaStatus();
    }
  }, [open]);

  const checkMfaStatus = async () => {
    setStep("LOADING");
    setError("");
    try {
      const response = await client.get("/api/cognito/mfa/status");
      setStep(response.mfaEnabled ? "ENABLED" : "INIT");
    } catch {
      setStep("INIT");
    }
  };

  const resetForm = () => {
    setStep("LOADING");
    setSecretCode("");
    setEmail("");
    setCode("");
    setError("");
    setLoading(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleStartSetup = async () => {
    setLoading(true);
    setError("");
    try {
      const accessToken = Cookies.get("access_token");
      if (!accessToken) {
        setError("Session expired. Please log out and log back in to set up MFA.");
        setLoading(false);
        return;
      }
      const response = await client.post("/api/cognito/mfa/setup", { accessToken });
      setSecretCode(response.secretCode);
      setEmail(response.email);
      setStep("QR");
    } catch (err) {
      setError("Failed to start MFA setup. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!code || code.length !== 6) {
      setError("Enter a 6-digit code");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const accessToken = Cookies.get("access_token");
      await client.post("/api/cognito/mfa/verify-setup", { accessToken, code });
      setStep("DONE");
    } catch (err) {
      setError("Invalid code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    setLoading(true);
    setError("");
    try {
      await client.post("/api/cognito/mfa/disable", {});
      setStep("DISABLED");
    } catch (err) {
      setError("Failed to disable MFA. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const otpauthUrl = `otpauth://totp/Paisley:${encodeURIComponent(email)}?secret=${secretCode}&issuer=Paisley`;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {step === "ENABLED" || step === "DISABLED" ? "Two-Factor Authentication" : "Set Up Two-Factor Authentication"}
      </DialogTitle>
      <DialogContent>
        {step === "LOADING" && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {step === "INIT" && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            Add an extra layer of security to your account. You'll need an authenticator
            app like Google Authenticator or Authy.
          </Typography>
        )}

        {step === "QR" && (
          <>
            <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
              Scan this QR code with your authenticator app, then enter the 6-digit code below.
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <QRCodeSVG value={otpauthUrl} size={200} />
            </Box>
            <Typography variant="caption" sx={{ display: "block", textAlign: "center", mb: 2, color: "text.secondary", wordBreak: "break-all" }}>
              Manual entry: {secretCode}
            </Typography>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            )}
            <TextField
              autoFocus
              fullWidth
              label="6-digit code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              onKeyDown={(e) => { if (e.key === "Enter" && !loading) handleVerify(); }}
              inputProps={{ maxLength: 6, style: { textAlign: "center", fontSize: "1.5rem", letterSpacing: "0.5em" } }}
            />
          </>
        )}

        {step === "DONE" && (
          <Alert severity="success" sx={{ mt: 1 }}>
            Two-factor authentication has been enabled. You'll be asked for a code from your authenticator app each time you log in.
          </Alert>
        )}

        {step === "ENABLED" && (
          <>
            <Alert severity="info" sx={{ mt: 1 }}>
              Two-factor authentication is currently active on your account.
            </Alert>
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
            )}
          </>
        )}

        {step === "DISABLED" && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Two-factor authentication has been disabled. You can re-enable it at any time.
          </Alert>
        )}

        {step === "INIT" && error && (
          <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {step === "INIT" && (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              onClick={handleStartSetup}
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : null}
            >
              Get Started
            </Button>
          </>
        )}
        {step === "QR" && (
          <>
            <Button onClick={handleClose} disabled={loading}>Cancel</Button>
            <Button
              onClick={handleVerify}
              variant="contained"
              disabled={loading || code.length !== 6}
              startIcon={loading ? <CircularProgress size={16} /> : null}
            >
              Verify & Enable
            </Button>
          </>
        )}
        {step === "DONE" && (
          <Button onClick={handleClose} variant="contained">Done</Button>
        )}
        {step === "ENABLED" && (
          <>
            <Button onClick={handleClose}>Close</Button>
            <Button
              onClick={handleDisable}
              variant="outlined"
              color="error"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : null}
            >
              Disable MFA
            </Button>
          </>
        )}
        {step === "DISABLED" && (
          <Button onClick={handleClose} variant="contained">Done</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MFASetupDialog;
